/* eslint-disable no-console */
import {
  QueryClient,
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { useRouter } from "next/router";

import {
  GetOrderArgs,
  GetOrderResponse,
  UpdateOrderItemArgs,
  UpdateOrderItemResponse,
  UseGetOrderResponse,
  UseUpdateOrderItemResponse,
} from "types/queries";
import {
  getMissingKeysFromObject,
  missingCookiesError,
} from "utils/error-utils";

import { useGetCsrfToken } from "./website-queries";

/**
 * Order query keys.
 *
 * These uniquely identify queries in the `react-query` cache.
 *
 * [More on this pattern](https://tkdodo.eu/blog/effective-react-query-keys)
 */
export const orderKeys = {
  order: (orderId: string) =>
    [{ scope: "order", orderId, data: "order" }] as const,
};

/**
 * Set defaults for order queries
 *
 * Setting these here applies them globally whether called directly or via custom hooks
 *
 * Also helpful for testing as they can be easily overriden
 * (e.g. to stop retries from slowing tests down)
 */
export function setOrderQueryDefaults(queryClient: QueryClient) {
  queryClient.setQueryDefaults([{ data: "order" }], {
    // Consider order data fresh for one minute
    staleTime: 60_000,
  });
}

/************************************************************************************************
 * QUERIES
 *
 * For each query, we define a hook and a fetcher function.
 *
 * Each hook wraps `useQuery` with configuration on when to fetch and parameters.
 *
 * The fetcher is separate from the hook for use outside of React components (e.g. on server).
 ************************************************************************************************/

/**
 * Get the user's order by id
 * Proxies to eagle via nginx
 */
// Prior to moving this to a direct eagle request, website v2 iterated over all customisations and transformed them from kebab case to snake case
// but this shouldnt be needed any more because all customisations are in snake case
async function getOrder({
  countryCode,
  csrfToken,
  currency,
  guestToken,
  locale,
  orderId,
  restrictedUserId,
}: GetOrderArgs) {
  const response = await axios.get<GetOrderResponse>(
    "/website/v2/orders/" + orderId,
    {
      headers: {
        Lmn_country_iso: countryCode,
        Lmn_currency: currency,
        Lmn_guest_token: guestToken,
        Lmn_restricted_user: restrictedUserId,
        Lmn_locale: locale,
        "X-Csrf-Token": csrfToken,
      },
    }
  );

  return response.data.data;
}

/**
 * Query to get the user's order.
 * Proxies to eagle via nginx
 */
export function useGetOrder() {
  const router = useRouter();

  const { data: csrfToken } = useGetCsrfToken();

  const orderId = router.query.order_id;

  if (!orderId || typeof orderId !== "string") {
    throw new Error("Missing or invalid order id in URL");
  }

  return useQuery<UseGetOrderResponse | null, AxiosError>({
    queryKey: orderKeys.order(orderId),
    queryFn: async () => {
      const {
        country_code: countryCode,
        currency,
        guest_token,
        guestToken,
        locale,
        restricted_user_id,
        restrictedUserId,
      } = Cookies.get();

      const missingCookies = getMissingKeysFromObject({
        countryCode,
        currency,
        locale,
      });

      if (missingCookies) {
        throw missingCookiesError(missingCookies);
      }

      return getOrder({
        countryCode,
        csrfToken: csrfToken!,
        currency,
        guestToken: guestToken ?? guest_token,
        orderId,
        locale,
        restrictedUserId: restrictedUserId ?? restricted_user_id,
      });
    },
    enabled: !!csrfToken,
  });
}

/**
 * Update the provided product in the user's order.
 * Proxies to eagle via nginx
 */
async function updateOrderItem({
  cartItemId,
  csrfToken,
  customisation,
}: UpdateOrderItemArgs): Promise<UseUpdateOrderItemResponse> {
  const {
    country_code,
    currency,
    guest_token,
    guestToken,
    locale,
    restricted_user_id,
    restrictedUserId,
  } = Cookies.get();

  const missingCookies = getMissingKeysFromObject({
    country_code,
    currency,
    locale,
  });

  if (missingCookies) {
    throw missingCookiesError(missingCookies);
  }

  await axios.put<UpdateOrderItemResponse>(
    "/website/v1/orders/items",
    {
      item: {
        id: cartItemId,
        customisations: customisation,
      },
    },
    {
      headers: {
        Lmn_locale: locale,
        Lmn_currency: currency,
        Lmn_country_iso: country_code,
        Lmn_guest_token: guestToken ?? guest_token,
        Lmn_restricted_user: restrictedUserId ?? restricted_user_id,
        "X-Csrf-Token": csrfToken,
      },
    }
  );

  return {};
}

/**
 * Mutation for updating the provided product in the user's order
 *
 * NOTE: CAN ONLY BE CALLED VIA WEBSITE PROXY
 */
export function useUpdateOrderItem(
  options: UseMutationOptions<
    UseUpdateOrderItemResponse,
    AxiosError,
    UpdateOrderItemArgs
  >
) {
  return useMutation([], updateOrderItem, options);
}
